export const DESKTOP_PROXY_MESSAGE_TYPE = "nprice:desktop-proxy";
export const DESKTOP_PROXY_READY_MESSAGE_TYPE = "nprice:desktop-proxy-ready";
/** 셸 → console: PC desktopId (로그인 시트 reclaim, 터널 전에도 가능) */
export const DESKTOP_DEVICE_MESSAGE_TYPE = "nprice:desktop-device";
export const DESKTOP_DEVICE_REQUEST_TYPE = "nprice:request-desktop-id";
/** console → 셸: 인증 후 최신 desktop proxy metadata 재요청 */
export const DESKTOP_PROXY_REQUEST_TYPE = "nprice:request-desktop-proxy";
/** desktop 셸 api 램프: console → parent */
export const DESKTOP_API_STATUS_MESSAGE_TYPE = "nprice:api-status";
/** desktop 셸 게이트: 프록시 등록 결과 console → parent */
export const DESKTOP_PROXY_REGISTER_MESSAGE_TYPE =
  "nprice:desktop-proxy-register";
/** 셸 → console: 앱 종료 전 현재 로그인 시트 해제 요청 */
export const DESKTOP_SHUTDOWN_REQUEST_TYPE = "nprice:desktop-shutdown";
/** console → 셸: 로그인 시트 해제 성공 확인 */
export const DESKTOP_SHUTDOWN_RESULT_TYPE = "nprice:desktop-shutdown-result";
/** console → 셸: 외부 URL을 시스템 기본 브라우저에서 열기 */
export const DESKTOP_EXTERNAL_URL_REQUEST_TYPE = "nprice:open-external-url";

const DESKTOP_ID_STORAGE_KEY = "nprice.desktopDeviceId";

export type DesktopApiStatusLevel = "idle" | "pending" | "ok" | "error";

export interface DesktopProxyRegistration {
  desktopId: string;
  remotePort: number;
}

export async function desktopProxyAccessKey(desktopId: string): Promise<string> {
  const canonicalId = desktopId.toLowerCase();
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(canonicalId));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function postToDesktopParent(payload: Record<string, unknown>): void {
  if (typeof window === "undefined" || window.parent === window) {
    return;
  }
  window.parent.postMessage(payload, "*");
}

/**
 * desktop 셸 안에서는 외부 URL을 parent가 시스템 기본 브라우저에서 연다.
 * 브라우저 단독 실행 시 false를 반환해 호출자가 기존 동작을 유지한다.
 */
export function requestDesktopExternalUrl(url: string): boolean {
  if (typeof window === "undefined" || window.parent === window) {
    return false;
  }
  window.parent.postMessage(
    { type: DESKTOP_EXTERNAL_URL_REQUEST_TYPE, url },
    "*",
  );
  return true;
}

export function openExternalUrl(url: string): void {
  if (requestDesktopExternalUrl(url)) {
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

/** desktop webview parent에 API 도달 상태를 알린다. 브라우저 단독 실행 시 no-op. */
export function postDesktopApiStatus(level: DesktopApiStatusLevel): void {
  postToDesktopParent({ type: DESKTOP_API_STATUS_MESSAGE_TYPE, level });
}

export function postDesktopShutdownResult(ok: boolean): void {
  postToDesktopParent({ type: DESKTOP_SHUTDOWN_RESULT_TYPE, ok });
}

/** desktop 인증 완료 후 현재 frpc remote port를 다시 받는다. */
export function requestDesktopProxyMetadata(): void {
  postToDesktopParent({ type: DESKTOP_PROXY_REQUEST_TYPE });
}

/** 등록 요청 직전 — 셸이 결과를 기다리게 한다. */
export function postDesktopProxyRegisterPending(): void {
  postToDesktopParent({
    type: DESKTOP_PROXY_REGISTER_MESSAGE_TYPE,
    status: "pending",
  });
}

/** 인증 해제 시 셸의 등록 대기/실패 게이트를 취소한다. */
export function postDesktopProxyRegisterIdle(): void {
  postToDesktopParent({
    type: DESKTOP_PROXY_REGISTER_MESSAGE_TYPE,
    status: "idle",
  });
}

/**
 * PC 프록시 등록 성공/실패를 셸에 알린다.
 * 실패 시 셸 intro로 막아 console이 infra error로 흘러가지 않게 한다.
 */
export function postDesktopProxyRegisterResult(
  ok: boolean,
  message?: string,
): void {
  postToDesktopParent({
    type: DESKTOP_PROXY_REGISTER_MESSAGE_TYPE,
    status: ok ? "ok" : "error",
    ok,
    ...(message ? { message } : {}),
  });
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** 셸에서 받은 desktopId 를 로그인에 쓴다 (sessionStorage). */
export function rememberDesktopDeviceId(desktopId: string): void {
  try {
    sessionStorage.setItem(DESKTOP_ID_STORAGE_KEY, desktopId);
  } catch {
    /* private mode */
  }
}

export function getRememberedDesktopDeviceId(): string | null {
  try {
    const v = sessionStorage.getItem(DESKTOP_ID_STORAGE_KEY);
    if (v && UUID_PATTERN.test(v)) {
      return v;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * desktop 셸 iframe 안에서 device id 를 확보한다.
 * 이미 저장돼 있으면 즉시, 아니면 parent 에 요청 후 대기.
 */
export function resolveDesktopDeviceId(timeoutMs = 2500): Promise<string | null> {
  if (typeof window === "undefined" || window.parent === window) {
    return Promise.resolve(null);
  }
  const cached = getRememberedDesktopDeviceId();
  if (cached) {
    return Promise.resolve(cached);
  }
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener("message", onMessage);
      resolve(getRememberedDesktopDeviceId());
    }, timeoutMs);
    function onMessage(event: MessageEvent) {
      if (event.source !== window.parent) {
        return;
      }
      const data = event.data as { type?: string; desktopId?: string };
      if (
        data?.type === DESKTOP_DEVICE_MESSAGE_TYPE &&
        typeof data.desktopId === "string" &&
        UUID_PATTERN.test(data.desktopId)
      ) {
        rememberDesktopDeviceId(data.desktopId);
        window.clearTimeout(timer);
        window.removeEventListener("message", onMessage);
        resolve(data.desktopId);
      }
    }
    window.addEventListener("message", onMessage);
    window.parent.postMessage({ type: DESKTOP_DEVICE_REQUEST_TYPE }, "*");
  });
}

export function parseDesktopProxyRegistration(
  data: unknown,
): DesktopProxyRegistration | null {
  return parseDesktopProxyPayload(data, DESKTOP_PROXY_MESSAGE_TYPE);
}

function parseDesktopProxyPayload(
  data: unknown,
  expectedType: string,
): DesktopProxyRegistration | null {
  if (!data || typeof data !== "object") {
    return null;
  }
  const message = data as {
    type?: unknown;
    registration?: { desktopId?: unknown; remotePort?: unknown };
  };
  if (message.type !== expectedType || !message.registration) {
    return null;
  }

  const { desktopId, remotePort } = message.registration;
  if (
    typeof desktopId !== "string" ||
    !UUID_PATTERN.test(desktopId) ||
    typeof remotePort !== "number" ||
    !Number.isInteger(remotePort) ||
    remotePort < 1 ||
    remotePort > 65535
  ) {
    return null;
  }
  return { desktopId, remotePort };
}
